/**
 * DOS518 - Digital Operations System for Wildfire Management
 * 
 * Copyright (c) 2024-2025 Angelo Irtilaç
 * All rights reserved.
 * 
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, modification, or use is strictly prohibited.
 * 
 * ---
 * 
 * Author: Angelo Irtilaç
 * Qualifications: DOS (Direttore delle Operazioni di Spegnimento)
 *                39 years - Corpo Nazionale dei Vigili del Fuoco
 *                DOS Trainer & Instructor
 * 
 * Location: Carpi, Italy
 * Contact: [inserire email]
 * 
 * ---
 * 
 * This software implements official Italian Fire Department protocols:
 * - C.O.A.U. (Centro Operativo Aereo Unificato)
 * - SI.TA.C. (Sistema Tattico di Coordinamento)
 * - L.A.C.E.S. (Lookout, Awareness, Communication, Escape routes, Safety zones)
 * 
 * Developed with operational expertise acquired through decades of wildfire
 * emergency management and coordination of suppression operations.
 * 
 * For commercial licensing inquiries, please contact the author.
 */
import React, { useState, useEffect } from 'react';
import { useAppStore } from '../../store/useAppStore';
import { SelectModal } from '../common/SelectModal';
import { MicInput } from '../common/MicInput';
import { checkinCountdown } from '../../hooks/useCheckinMonitor';
import { TRIGGER_PREDEFINITI } from '../../config/triggerAllerta';

import type { Lookout } from '../../types';

const COMUNICAZIONI = ['Radio', 'Telefono', 'Radio/Telefono', 'Visiva', 'Altro'];
const DOTAZIONE_ITEMS = [
  { id: 'Drone',       label: '🚁 Drone'       },
  { id: 'Termocamera', label: '🌡️ Termocamera'  },
  { id: 'GPS',         label: '📡 GPS'          },
];
const STATI = ['Attiva', 'Stand-by'];
const LIVELLI = ['🌱 1° anno AIB', '⭐ Esperto AIB', '🏅 DOS'];
const LIVELLO_DISPLAY: Record<string, string> = {
  primo_anno: '🌱 1° anno AIB',
  esperto:    '⭐ Esperto AIB',
  dos:        '🏅 DOS',
};
const LIVELLO_VALUE: Record<string, string> = {
  '🌱 1° anno AIB': 'primo_anno',
  '⭐ Esperto AIB': 'esperto',
  '🏅 DOS':         'dos',
};

const STATO_OP_COLOR: Record<string, string> = {
  in_posizione: '#22c55e',
  non_risponde: '#eab308',
  ritirata:     '#ef4444',
};
const STATO_OP_LABEL: Record<string, string> = {
  in_posizione: '🟢 In posizione',
  non_risponde: '🟡 Non risponde',
  ritirata:     '🔴 Ritirata',
};

export const LookutsManagement: React.FC = () => {
  const lookouts = useAppStore((state) => state.lookouts);
  const addLookout = useAppStore((state) => state.addLookout);
  const updateLookout = useAppStore((state) => state.updateLookout);
  const deleteLookout = useAppStore((state) => state.deleteLookout);

  // Tick ogni 30 s per aggiornare i countdown
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 30_000);
    return () => window.clearInterval(id);
  }, []);

  const [formData, setFormData] = useState({
    nominativo: '',
    posizione: '',
    settore: '',
    comunicazione: '',
    stato: '' as 'attiva' | 'standby' | '',
    canalRadio: '',
    livelloEsperienza: '' as 'primo_anno' | 'esperto' | 'dos' | '',
    checkinIntervalMin: 15,
    triggerMonitorati: [] as string[],
    dotazione: [] as string[],
  });

  const [editingId, setEditingId] = useState<number | null>(null);
  const [showComunicazioneModal, setShowComunicazioneModal] = useState(false);
  const [showStatoModal, setShowStatoModal] = useState(false);
  const [showLivelloModal, setShowLivelloModal] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleComunicazioneSelect = (value: string) => {
    setFormData((prev) => ({ ...prev, comunicazione: value }));
    setShowComunicazioneModal(false);
  };

  const handleStatoSelect = (value: string) => {
    const statoValue = value === 'Attiva' ? 'attiva' : 'standby';
    setFormData((prev) => ({ ...prev, stato: statoValue as 'attiva' | 'standby' }));
    setShowStatoModal(false);
  };

  const handleLivelloSelect = (value: string) => {
    setFormData((prev) => ({ ...prev, livelloEsperienza: (LIVELLO_VALUE[value] ?? '') as typeof prev.livelloEsperienza }));
    setShowLivelloModal(false);
  };

  const handleSubmit = () => {
    // Validazione
    if (!formData.nominativo || !formData.posizione || !formData.settore || 
        !formData.comunicazione || !formData.stato) {
      alert('⚠️ Tutti i campi sono obbligatori per salvare la vedetta.');
      return;
    }

    const extra = {
      canalRadio:          formData.canalRadio || undefined,
      livelloEsperienza:   (formData.livelloEsperienza || undefined) as 'primo_anno' | 'esperto' | 'dos' | undefined,
      checkinIntervalMin:  formData.checkinIntervalMin,
      triggerMonitorati:   formData.triggerMonitorati.length > 0 ? formData.triggerMonitorati : undefined,
      dotazione:           formData.dotazione.length > 0 ? formData.dotazione : undefined,
    };
    if (editingId) {
      updateLookout(editingId, {
        ...formData,
        stato: formData.stato as 'attiva' | 'standby',
        ...extra,
      });
    } else {
      addLookout({
        nominativo: formData.nominativo,
        posizione: formData.posizione,
        settore: formData.settore,
        comunicazione: formData.comunicazione,
        stato: formData.stato as 'attiva' | 'standby',
        ...extra,
      });
    }

    // Reset form
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      nominativo: '',
      posizione: '',
      settore: '',
      comunicazione: '',
      stato: '',
      canalRadio: '',
      livelloEsperienza: '',
      checkinIntervalMin: 15,
      triggerMonitorati: [],
      dotazione: [],
    });
    setEditingId(null);
  };

  const handleEdit = (lookout: Lookout) => {
    setFormData({
      nominativo: lookout.nominativo,
      posizione: lookout.posizione,
      settore: lookout.settore,
      comunicazione: lookout.comunicazione,
      stato: lookout.stato,
      canalRadio: lookout.canalRadio ?? '',
      livelloEsperienza: lookout.livelloEsperienza ?? '',
      checkinIntervalMin: lookout.checkinIntervalMin ?? 15,
      triggerMonitorati: lookout.triggerMonitorati ?? [],
      dotazione: lookout.dotazione ?? [],
    });
    setEditingId(lookout.id);
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Sei sicuro di voler eliminare questa vedetta?')) {
      deleteLookout(id);
      if (editingId === id) {
        resetForm();
      }
    }
  };

  return (
    <div className="lookouts-management-section">
      <h3>👁️ Gestione Vedette</h3>

      {/* FORM SEMPRE VISIBILE */}
      <div className="form-container">
        <div className="form-row">
          <div className="form-group">
            <label>👤 Nominativo *</label>
            <MicInput
              type="text"
              name="nominativo"
              value={formData.nominativo}
              onChange={(e) => setFormData((p) => ({ ...p, nominativo: e.target.value }))}
              placeholder="Nome operatore designato"
            />
          </div>
          <div className="form-group">
            <label>📍 Posizione *</label>
            <MicInput
              type="text"
              name="posizione"
              value={formData.posizione}
              onChange={(e) => setFormData((p) => ({ ...p, posizione: e.target.value }))}
              placeholder="Coordinate o descrizione"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>🔍 Settore Sorvegliato *</label>
            <MicInput
              type="text"
              name="settore"
              value={formData.settore}
              onChange={(e) => setFormData((p) => ({ ...p, settore: e.target.value }))}
              placeholder="Area di responsabilità"
            />
          </div>
          <div className="form-group">
            <label>📡 Comunicazione *</label>
            <input
              type="text"
              value={formData.comunicazione}
              placeholder="Seleziona comunicazione..."
              readOnly
              onClick={() => setShowComunicazioneModal(true)}
              style={{ cursor: 'pointer' }}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>⚡ Stato *</label>
            <input
              type="text"
              value={formData.stato === 'attiva' ? 'Attiva' : formData.stato === 'standby' ? 'Stand-by' : ''}
              placeholder="Seleziona stato..."
              readOnly
              onClick={() => setShowStatoModal(true)}
              style={{ cursor: 'pointer' }}
            />
          </div>
          <div className="form-group">
            <label>📻 Canale Radio</label>
            <MicInput
              type="text"
              name="canalRadio"
              value={formData.canalRadio}
              onChange={(e) => setFormData((p) => ({ ...p, canalRadio: e.target.value }))}
              placeholder="Es. CH3 / VHF 162.4"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>🎖️ Livello Esperienza AIB</label>
            <input
              type="text"
              value={formData.livelloEsperienza ? (LIVELLO_DISPLAY[formData.livelloEsperienza] ?? '') : ''}
              placeholder="Seleziona livello..."
              readOnly
              onClick={() => setShowLivelloModal(true)}
              style={{ cursor: 'pointer' }}
            />
          </div>
          <div className="form-group">
            <label>⏱️ Check-in ogni (min)</label>
            <input
              type="number"
              min={5} max={60} step={5}
              value={formData.checkinIntervalMin}
              onChange={(e) => setFormData((p) => ({ ...p, checkinIntervalMin: Math.max(5, parseInt(e.target.value) || 15) }))}
              style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.15)', background: '#1a2235', color: '#e2e8f0', fontSize: '0.9rem' }}
            />
          </div>
        </div>

        {/* Dotazione attrezzatura */}
        <div className="form-group" style={{ margin: '0 0 10px' }}>
          <label style={{ marginBottom: 6, display: 'block' }}>🎒 Dotazione Attrezzatura</label>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {DOTAZIONE_ITEMS.map((item) => {
              const sel = formData.dotazione.includes(item.id);
              return (
                <label key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: '0.88rem', color: sel ? '#38bdf8' : '#94a3b8', fontWeight: sel ? 700 : 400 }}>
                  <input
                    type="checkbox"
                    checked={sel}
                    onChange={() => setFormData((p) => ({
                      ...p,
                      dotazione: sel
                        ? p.dotazione.filter((x) => x !== item.id)
                        : [...p.dotazione, item.id],
                    }))}
                    style={{ accentColor: '#38bdf8', width: 16, height: 16 }}
                  />
                  {item.label}
                </label>
              );
            })}
          </div>
        </div>

        {/* Trigger di allerta pre-configurabili */}
        <div className="form-group" style={{ margin: '0 0 10px' }}>
          <label style={{ marginBottom: 6, display: 'block' }}>🔔 Trigger da Monitorare</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {TRIGGER_PREDEFINITI.map((t) => {
              const sel = formData.triggerMonitorati.includes(t);
              return (
                <label key={t} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: '0.8rem', color: sel ? '#f97316' : '#94a3b8' }}>
                  <input
                    type="checkbox"
                    checked={sel}
                    onChange={() => setFormData((p) => ({
                      ...p,
                      triggerMonitorati: sel
                        ? p.triggerMonitorati.filter((x) => x !== t)
                        : [...p.triggerMonitorati, t],
                    }))}
                    style={{ accentColor: '#f97316' }}
                  />
                  {t}
                </label>
              );
            })}
          </div>
        </div>

        <div className="form-actions">
          <button className="btn btn-primary-lookout" onClick={handleSubmit}>
            💾 {editingId ? 'Aggiorna' : 'Salva'}
          </button>
          {editingId && (
            <button className="btn btn-secondary" onClick={resetForm}>
              ❌ Annulla
            </button>
          )}
        </div>
      </div>

      {/* MODALI */}
      <SelectModal
        isOpen={showComunicazioneModal}
        onClose={() => setShowComunicazioneModal(false)}
        title="📡 Seleziona Comunicazione"
        options={COMUNICAZIONI}
        onSelect={handleComunicazioneSelect}
      />

      <SelectModal
        isOpen={showStatoModal}
        onClose={() => setShowStatoModal(false)}
        title="⚡ Seleziona Stato"
        options={STATI}
        onSelect={handleStatoSelect}
      />

      <SelectModal
        isOpen={showLivelloModal}
        onClose={() => setShowLivelloModal(false)}
        title="🎖️ Livello Esperienza AIB"
        options={LIVELLI}
        onSelect={handleLivelloSelect}
      />

      {/* LISTA VEDETTE */}
      <h4 className="lista-title">📋 Lista Vedette ({lookouts.length})</h4>
      <div id="lista-vedette">
        {lookouts.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#00d4ff', padding: '20px' }}>
            Nessuna vedetta configurata. Compila il form sopra per iniziare.
          </p>
        ) : (
          lookouts.map((lookout) => {
            const statoOpColor = lookout.statoOperativo ? STATO_OP_COLOR[lookout.statoOperativo] : undefined;
            return (
              <div key={lookout.id} className="lookout-card" style={statoOpColor ? { borderLeft: `4px solid ${statoOpColor}` } : undefined}>
                <div className="card-header-risorsa">
                  <h4>
                    🔭 {lookout.nominativo}
                    <span className={`badge ${lookout.stato === 'attiva' ? 'badge-success' : 'badge-warning'}`}>
                      {lookout.stato === 'attiva' ? 'ATTIVA' : 'STAND-BY'}
                    </span>
                  </h4>
                  <div className="card-actions">
                    <button className="btn-icon btn-edit" onClick={() => handleEdit(lookout)} title="Modifica">
                      ✏️
                    </button>
                    <button className="btn-icon btn-delete" onClick={() => handleDelete(lookout.id)} title="Elimina">
                      🗑️
                    </button>
                  </div>
                </div>

                {/* Stato operativo rapido */}
                <div style={{ display: 'flex', gap: 6, padding: '6px 12px 4px', flexWrap: 'wrap' }}>
                  {(['in_posizione', 'non_risponde', 'ritirata'] as const).map((s) => (
                    <button
                      key={s}
                      onClick={() => updateLookout(lookout.id, { statoOperativo: s })}
                      style={{
                        padding: '3px 8px', borderRadius: 4,
                        border: `1.5px solid ${STATO_OP_COLOR[s]}`,
                        background: lookout.statoOperativo === s ? STATO_OP_COLOR[s] : 'transparent',
                        color: lookout.statoOperativo === s ? '#fff' : STATO_OP_COLOR[s],
                        fontSize: '0.75rem', cursor: 'pointer', fontWeight: 600,
                      }}
                    >
                      {STATO_OP_LABEL[s]}
                    </button>
                  ))}
                </div>

                {/* Check-in timer — solo vedette attive */}
                {lookout.stato === 'attiva' && (() => {
                  const cd = checkinCountdown(lookout.ultimoCheckin, lookout.checkinIntervalMin ?? 15);
                  const overdue = cd?.overdue ?? false;
                  return (
                    <div style={{
                      margin: '4px 12px 6px',
                      padding: '6px 10px',
                      borderRadius: 6,
                      background: overdue ? 'rgba(220,38,38,0.12)' : 'rgba(5,150,105,0.08)',
                      border: `1px solid ${overdue ? 'rgba(220,38,38,0.4)' : 'rgba(5,150,105,0.3)'}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                    }}>
                      <div>
                        {cd === null && (
                          <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                            ⏱️ Check-in ogni {lookout.checkinIntervalMin ?? 15} min — premi Confermato dopo primo contatto
                          </span>
                        )}
                        {cd !== null && overdue && (
                          <span style={{ fontSize: '0.78rem', color: '#ef4444', fontWeight: 700 }}>
                            🚨 Check-in SCADUTO da {Math.abs(cd.remainingMin)} min
                          </span>
                        )}
                        {cd !== null && !overdue && (
                          <span style={{ fontSize: '0.75rem', color: cd.remainingMin <= 5 ? '#eab308' : '#22c55e' }}>
                            ✅ Prossimo check-in tra {cd.remainingMin} min
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => updateLookout(lookout.id, { ultimoCheckin: new Date().toISOString() })}
                        style={{
                          padding: '4px 10px', borderRadius: 5, border: 'none', cursor: 'pointer',
                          background: overdue ? '#dc2626' : '#059669',
                          color: '#fff', fontSize: '0.78rem', fontWeight: 700, whiteSpace: 'nowrap',
                        }}
                      >
                        ✅ Confermato
                      </button>
                    </div>
                  );
                })()}

                {/* Trigger di allerta */}
                {(lookout.triggerMonitorati?.length ?? 0) > 0 && (
                  <div style={{ margin: '0 12px 6px' }}>
                    <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginBottom: 4 }}>🔔 Trigger allerta</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {lookout.triggerMonitorati!.map((t) => {
                        const attivo = lookout.triggerAttivi?.includes(t);
                        return (
                          <button
                            key={t}
                            onClick={() => {
                              const curr = lookout.triggerAttivi ?? [];
                              updateLookout(lookout.id, {
                                triggerAttivi: attivo ? curr.filter((x) => x !== t) : [...curr, t],
                              });
                            }}
                            style={{
                              padding: '3px 7px', borderRadius: 3, fontSize: '0.74rem', cursor: 'pointer',
                              border: `1px solid ${attivo ? '#f97316' : 'rgba(255,255,255,0.2)'}`,
                              background: attivo ? 'rgba(249,115,22,0.15)' : 'transparent',
                              color: attivo ? '#f97316' : '#64748b',
                              fontWeight: attivo ? 700 : 400,
                            }}
                          >
                            {attivo ? '🚨' : '○'} {t}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="card-body-risorsa">
                  <div className="info-row">
                    <span className="info-label">📍 Posizione:</span>
                    <span className="info-value">{lookout.posizione}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">🔍 Settore:</span>
                    <span className="info-value">{lookout.settore}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">📡 Comunicazione:</span>
                    <span className="info-value">{lookout.comunicazione}</span>
                  </div>
                  {lookout.canalRadio && (
                    <div className="info-row">
                      <span className="info-label">📻 Canale:</span>
                      <span className="info-value">{lookout.canalRadio}</span>
                    </div>
                  )}
                  {lookout.livelloEsperienza && (
                    <div className="info-row">
                      <span className="info-label">🎖️ Esperienza:</span>
                      <span className="info-value">
                        {lookout.livelloEsperienza === 'primo_anno' ? '🌱 1° anno' : lookout.livelloEsperienza === 'esperto' ? '⭐ Esperto' : '🏅 DOS'}
                      </span>
                    </div>
                  )}
                  {lookout.lat !== undefined && lookout.lng !== undefined && (
                    <div className="info-row">
                      <span className="info-label">🗺️ Mappa:</span>
                      <span className="info-value" style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                        {lookout.lat.toFixed(5)}, {lookout.lng.toFixed(5)}
                      </span>
                    </div>
                  )}
                  {lookout.lat === undefined && (
                    <div className="info-row" style={{ color: '#eab308', fontSize: '0.75rem' }}>
                      ⚠️ Posizione non ancora segnata su SI.TA.C.
                    </div>
                  )}
                  {(lookout.dotazione?.length ?? 0) > 0 && (
                    <div className="info-row" style={{ alignItems: 'center' }}>
                      <span className="info-label">🎒 Dotazione:</span>
                      <span className="info-value" style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                        {lookout.dotazione!.map((d) => {
                          const item = DOTAZIONE_ITEMS.find((i) => i.id === d);
                          return (
                            <span key={d} style={{ background: 'rgba(56,189,248,0.15)', border: '1px solid rgba(56,189,248,0.4)', color: '#38bdf8', borderRadius: 4, padding: '1px 7px', fontSize: '0.78rem', fontWeight: 600 }}>
                              {item?.label ?? d}
                            </span>
                          );
                        })}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
